import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { catchError, delay, filter, Observable, of, switchMap, take } from 'rxjs';
import { Injectable, Injector } from '@angular/core';
import { AlertService } from './_services/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { ConnectionStatusService } from './_services/connection-status.service';
import { AuthorizationService } from './_services/authorization.service';

/** Ekran sa statusom veze (login, traka u aplikaciji) se pojavi tek posle odluke o prijavi i
 *  navigacije (guard → ruta) — kratko sačekaj da on preuzme, umesto da poruka bljesne. */
const START_SCREEN_SETTLE_MS = 1500;

@Injectable()
export class OfflineDetectorInterceptor implements HttpInterceptor {
  // Holds the open "no internet" dialog so we can close it on reconnect
  // (replaces Swal.isVisible() guard + Swal.close()).
  private offlineRef?: ReturnType<AlertService['openRef']>;
  /** Poruka je otvorena ili se upravo otvara (čeka prevod) — drugi zahtev je ne otvara ponovo. */
  private offlineShown = false;
  /** Korisnik je zatvorio poruku dok nema mreže — ne otvaraj je ponovo dok se veza ne vrati
   *  (zahtevi u pozadini idu na par sekundi i otvarali bi je iznova). */
  private dismissedWhileOffline = false;
  /** Poruka koju je zatvorila sama aplikacija (povratak veze, status veze na ekranu) — to nije OK korisnika. */
  private closedByApp?: ReturnType<AlertService['openRef']>;

  // ConnectionStatusService je bez zavisnosti, pa sme u konstruktor (za razliku od Translate/Alert).
  constructor(private injector: Injector,
    private connectionStatus: ConnectionStatusService) {
    // Veza se vratila — skloni poruku i bez novog zahteva (ekran bez tastature/miša ne može na OK).
    window.addEventListener('online', () => this.onOnline());

    // Ekran sa statusom veze (login, traka u aplikaciji) kaže isto i više — poruka otvorena pre
    // njega (prvi zahtev pri startu ide pre nego što se ekran prikaže) se sklanja.
    connectionStatus.mounted$.subscribe(count => {
      if (count > 0) {
        this.closeDialog();
      }
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // check to see if there's internet
    if (!window.navigator.onLine) {
      // Ručna prijava kodom sama prikazuje razlog i retry (code-signin); modal bi ga prekrio.
      if (!request.url.includes('SigninCode/')) {
        this.showOfflineDialog();
      }

      // if there is no internet, throw a HttpErrorResponse error
      // since an error is thrown, the function will terminate here
      throw new HttpErrorResponse({ error: 'Internet is required.' });

    } else {
      this.onOnline();
      // else return the normal request
      return next.handle(request);
    }
  }

  private showOfflineDialog() {
    // na ekranu sa statusom veze (login, traka u aplikaciji) poruka je suvišna
    if (this.offlineShown || this.dismissedWhileOffline || this.connectionStatus.inlineMounted) {
      return;
    }
    this.offlineShown = true;

    // Odluka tek posle tekućeg makrotaska: zahtev može da potekne iz konstruktora servisa koji je
    // još u izgradnji, pa injector.get(...) ovde ne sme sinhrono (NG0200).
    setTimeout(() => this.openOfflineDialog());
  }

  private openOfflineDialog() {
    // TranslateService/AlertService/AuthorizationService se uzimaju tek ovde: sam load prevoda ide
    // kroz HttpClient, pa bi injektovanje pri svakom zahtevu napravilo
    // cirkularnu zavisnost (NG0200) dok se TranslateService još konstruiše.
    const translate = this.injector.get(TranslateService);
    const alert = this.injector.get(AlertService);
    const auth = this.injector.get(AuthorizationService);

    // Pri startu app još ne zna početni ekran (čita sačuvanu prijavu) — sačekaj odluku i ekran, pa
    // se poruka ne otvara ako na njemu stoji status veze (KD rutira na login tek posle čitanja).
    const startScreenKnown = (auth.tokenLoaded.value
      ? of(true)
      : auth.tokenLoaded.pipe(filter(loaded => loaded), take(1))
    ).pipe(delay(START_SCREEN_SETTLE_MS));

    startScreenKnown.pipe(
      // get(), ne instant(): prvi zahtev ide pri startu, pre nego što se prevodi učitaju —
      // instant() bi tada vratio sam ključ ("offlineNoInternet").
      switchMap(() => translate.get('offlineNoInternet')),
      take(1),
      catchError(() => of(translate.instant('offlineNoInternet')))
    ).subscribe(title => {
      if (window.navigator.onLine || this.connectionStatus.inlineMounted) {
        // dok se prevod učitavao veza se vratila, ili se pojavio ekran sa statusom veze
        this.offlineShown = false;
        return;
      }

      const ref = alert.openRef({ type: 'error', title });
      this.offlineRef = ref;
      ref.afterClosed().subscribe(() => {
        const byApp = this.closedByApp === ref;
        if (byApp) {
          this.closedByApp = undefined;
        }
        if (this.offlineRef === ref) {
          this.offlineRef = undefined;
        }
        this.offlineShown = false;
        // Korisnik je kliknuo OK dok mreže i dalje nema — ne otvaraj ponovo do povratka veze.
        if (!byApp && !window.navigator.onLine) {
          this.dismissedWhileOffline = true;
        }
      });
    });
  }

  private onOnline() {
    this.dismissedWhileOffline = false;
    this.closeDialog();
  }

  private closeDialog() {
    if (this.offlineRef) {
      this.closedByApp = this.offlineRef;
      this.offlineRef.close();
      this.offlineRef = undefined;
    }
  }
}
